import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format } from 'date-fns/format';
import { parseISO } from 'date-fns/parseISO';
import Layout from '../components/Layout';
import { getAvailableInterviewTimes, bookInterview, InterviewSlot } from '../utils/api';
import { usePageTracking, useAnalytics } from '../hooks/useAnalytics';

type BookingStatus = 'idle' | 'loading' | 'ready' | 'booking' | 'success' | 'error';

const Interview: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<BookingStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [availableSlots, setAvailableSlots] = useState<InterviewSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<InterviewSlot | null>(null);
  const [groupedSlots, setGroupedSlots] = useState<{ [date: string]: InterviewSlot[] }>({});

  usePageTracking('Interview (Legacy)');
  const { trackInterviewScheduled, trackAPIError } = useAnalytics();

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('No token provided. Please use the link from your email.');
      return;
    }

    loadAvailableSlots();
  }, [token]);

  const loadAvailableSlots = async () => {
    if (!token) return;

    setStatus('loading');
    try {
      const response = await getAvailableInterviewTimes(token);

      if (response.error) {
        setStatus('error');
        setErrorMessage(response.error);
      } else if (response.availableSlots.length === 0) {
        setStatus('error');
        setErrorMessage('No interview slots are currently available. Please check back later or contact support.');
      } else {
        setAvailableSlots(response.availableSlots);

        // Group slots by date
        const grouped: { [date: string]: InterviewSlot[] } = {};
        response.availableSlots.forEach((slot) => {
          const dateKey = format(parseISO(slot.start), 'EEEE, MMMM d, yyyy');
          if (!grouped[dateKey]) {
            grouped[dateKey] = [];
          }
          grouped[dateKey].push(slot);
        });
        setGroupedSlots(grouped);

        setStatus('ready');
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage('Failed to load available times. Please try again.');
    }
  };

  const handleSelectSlot = (slot: InterviewSlot) => {
    setSelectedSlot(slot);
    setErrorMessage('');
  };

  const handleBookInterview = async () => {
    if (!selectedSlot || !token) return;

    setStatus('booking');
    setErrorMessage('');

    try {
      const response = await bookInterview({
        token,
        interviewTime: selectedSlot.start,
      });

      if (response.success) {
        trackInterviewScheduled({ selectedDate: format(parseISO(selectedSlot.start), 'yyyy-MM-dd'), selectedTime: selectedSlot.displayTime || '', bookingSource: 'legacy' });
        setStatus('success');
      } else {
        setStatus('error');
        setErrorMessage(response.error || 'Failed to book interview. Please try again.');
      }
    } catch (error) {
      trackAPIError({ endpoint: '/api/interview/book', errorCode: 'unknown', errorMessage: 'Failed to book interview' });
      setStatus('error');
      setErrorMessage('An unexpected error occurred. Please try again.');
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto animate-fade-in">
        <div className="card p-8">
          <h1 className="text-3xl font-bold text-charcoal mb-2">Schedule Your Interview</h1>
          <p className="text-gray-600 mb-8">
            Select a time slot that works best for you. We'll send you a calendar invite once confirmed.
          </p>

          {status === 'loading' && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-logo-green mx-auto mb-4"></div>
              <p className="text-gray-600">Loading available times...</p>
            </div>
          )}

          {status === 'error' && (
            <div className="bg-alert-red/10 border border-alert-red text-alert-red rounded-lg p-4 mb-6">
              <p className="font-semibold">Error</p>
              <p>{errorMessage}</p>
            </div>
          )}

          {status === 'ready' && (
            <>
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-charcoal mb-4">Available Time Slots</h2>

                <div className="space-y-6">
                  {Object.entries(groupedSlots).map(([date, slots]) => (
                    <div key={date}>
                      <h3 className="text-lg font-semibold text-logo-green mb-3">{date}</h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {slots.map((slot, index) => (
                          <button
                            key={index}
                            onClick={() => handleSelectSlot(slot)}
                            className={`p-3 rounded-lg border-2 transition-all duration-200 ${
                              selectedSlot?.start === slot.start
                                ? 'border-logo-green bg-logo-green text-white'
                                : 'border-gray-300 bg-white text-charcoal hover:border-logo-green'
                            }`}
                          >
                            <div className="text-sm font-semibold">{slot.displayTime}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {selectedSlot && (
                <div className="bg-logo-green/10 border border-logo-green rounded-lg p-4 mb-6">
                  <p className="text-sm text-gray-600">Selected time:</p>
                  <p className="font-semibold text-charcoal">
                    {format(parseISO(selectedSlot.start), 'EEEE, MMMM d, yyyy')} at {selectedSlot.displayTime}
                  </p>
                </div>
              )}

              {errorMessage && (
                <div className="bg-alert-red/10 border border-alert-red text-alert-red rounded-lg p-4 mb-6">
                  <p>{errorMessage}</p>
                </div>
              )}

              <button
                onClick={handleBookInterview}
                disabled={!selectedSlot}
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Confirm Interview Time
              </button>
            </>
          )}

          {status === 'booking' && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-logo-green mx-auto mb-4"></div>
              <p className="text-gray-600">Booking your interview...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-logo-green/10 rounded-full mb-4">
                <svg className="w-8 h-8 text-logo-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-charcoal mb-2">Interview Scheduled!</h2>
              <p className="text-gray-600 mb-4">
                Your interview has been successfully scheduled for:
              </p>
              {selectedSlot && (
                <div className="bg-logo-green/10 border border-logo-green rounded-lg p-4 mb-6 inline-block">
                  <p className="font-semibold text-charcoal text-lg">
                    {format(parseISO(selectedSlot.start), 'EEEE, MMMM d, yyyy')}
                  </p>
                  <p className="text-charcoal">{selectedSlot.displayTime}</p>
                </div>
              )}
              <p className="text-sm text-gray-500">
                You will receive a calendar invite and reminder email shortly.
              </p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Interview;
